import { useEffect, useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import moment from 'moment';

import { PraSection } from '../../../../../../__gqltypes__';
import { formatHms } from '../utils';

type Props = {
  section: PraSection;
  configExists: boolean;
  maintenance: boolean;
  maintenanceSince?: Date | string | number | null;
  // maintenanceSince + this section's SAVED cooldownSeconds + 15 min buffer (D8).
  safeToEditAt: Date | null;
  saving: boolean;
  onToggle: (enabled: boolean) => void;
};

// Per-section maintenance switch (split per section, D13) with the live safe-to-edit display.
export function PraMaintenancePanel({
  section,
  configExists,
  maintenance,
  maintenanceSince,
  safeToEditAt,
  saving,
  onToggle,
}: Props): JSX.Element {
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!maintenance) {
      return undefined;
    }
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [maintenance]);

  const safeInMs = safeToEditAt ? safeToEditAt.getTime() - now : null;
  const isSafe = safeInMs !== null && safeInMs <= 0;

  return (
    <Form.Group>
      <Form.Check
        type="switch"
        id={`pra-maintenance-${section}`}
        label="Maintenance mode (players without an active cycle see the maintenance screen; mid-cycle players finish on their snapshot)"
        checked={maintenance}
        disabled={!configExists || saving}
        onChange={(e) => onToggle(e.target.checked)}
      />
      {!configExists && (
        <Form.Text className="text-muted">Save this section once to enable the maintenance switch.</Form.Text>
      )}
      {maintenance && (
        <Alert variant={isSafe ? 'success' : 'warning'} className="mt-2 mb-0">
          <div>
            Maintenance since:{' '}
            <strong>{maintenanceSince != null ? moment(maintenanceSince).format('YYYY-MM-DD HH:mm:ss') : '—'}</strong>
          </div>
          <div>
            Safe to edit at:{' '}
            <strong>{safeToEditAt ? moment(safeToEditAt).format('YYYY-MM-DD HH:mm:ss') : '—'}</strong>{' '}
            <span className="text-muted">(since + this section&apos;s cooldown + 15 min buffer)</span>
          </div>
          <div className="font-weight-bold">
            {isSafe && 'SAFE NOW ✓'}
            {!isSafe && safeInMs !== null && `safe in ${formatHms(safeInMs)}`}
            {safeInMs === null && '—'}
          </div>
        </Alert>
      )}
    </Form.Group>
  );
}
